import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Typography, Breadcrumbs, Link, Box } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useAuth } from '@contexts/AuthContext';
import API from '@utils/axios';
import { formError } from '@utils/helpers';
import Layout from '@components/Layout';
import UserForm from '@components/user/UserForm';
import UserApiKey from '@components/user/UserApiKey';
import FormSkeleton from '@components/FormSkeleton';

function UserEditPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { showMessage } = useSnackbar();
    const { refreshAuth } = useAuth();
    const [initialData, setInitialData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const form = useRef();

    const fetchData = useCallback(async () => {
        try {
            const response = await API.get(`/users/${id}`);
            setInitialData(response.data);
        } catch (error) {
            showMessage(t('Failed to load data'), 'error');
        } finally {
            setLoaded(true);
        }
    }, [id, t, showMessage]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmit = async (data) => {
        try {
            if (data.password === '') {
                delete data.password;
            }
            if (typeof data.password_confirmation !== 'undefined') {
                delete data.password_confirmation;
            }
            await API.put(`/users/${id}`, data);
            showMessage(t('User updated successfully'), 'success');
            // Vider les mots de passe des champs
            setInitialData({ ...data, password: '', password_confirmation: '' });
            refreshAuth();
        } catch (error) {
            formError(error, t, form.current.setError, showMessage);
        }
    };

    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to="/users" underline="hover">{t('User management')}</Link>
                    <Typography>{t('Edit user')}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to="/users" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            {loaded ?
                <>
                    <UserForm initialData={initialData} onSubmit={handleSubmit} ref={form} passwordRequired={false}>
                        <UserApiKey user={initialData} generateUrl={`/users/${id}/generate-api-key`} onGenerated={fetchData} />
                    </UserForm>
                </>
                : <FormSkeleton nb={9} />
            }
        </Layout>
    );
}

export default UserEditPage;
